import { useState, useEffect, useCallback } from "react";
import { getReport, getFornitori, getClienti, getProdotti } from "@/lib/api";
import { formatDate, formatNumber } from "@/lib/utils";
import { exportReportToPDF, exportToExcel } from "@/lib/export";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  FileText, 
  Download, 
  FileSpreadsheet, 
  Filter,
  ArrowDownToLine,
  ArrowUpFromLine,
  Search,
  RotateCcw,
  Layers,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Report() {
  const [movimenti, setMovimenti] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [fornitori, setFornitori] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState({ entrate: 0, uscite: 0, scarto: 0, grossa: 0, media: 0, bolla: 0, count: 0 });
  const [groupByPartita, setGroupByPartita] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc"); // desc = più recenti, asc = più vecchi
  
  const [filters, setFilters] = useState({
    fornitore: "",
    cliente: "",
    prodotto: "",
    codice_partita: "",
    tipo: "",
    data_da: "",
    data_a: "",
  });

  useEffect(() => {
    loadDropdowns();
  }, []);

  const loadDropdowns = async () => {
    try {
      const [fornitoriRes, clientiRes, prodottiRes] = await Promise.all([
        getFornitori(),
        getClienti(),
        getProdotti(),
      ]);
      setFornitori(fornitoriRes.data);
      setClienti(clientiRes.data);
      setProdotti(prodottiRes.data);
    } catch (error) {
      console.error("Error loading dropdowns:", error);
    }
  };

  const handleSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.fornitore) params.fornitore = filters.fornitore;
      if (filters.cliente) params.cliente = filters.cliente;
      if (filters.prodotto) params.prodotto = filters.prodotto;
      if (filters.codice_partita) params.codice_partita = filters.codice_partita;
      if (filters.tipo) params.tipo = filters.tipo;
      if (filters.data_da) params.data_da = filters.data_da;
      if (filters.data_a) params.data_a = filters.data_a;

      const response = await getReport(params);
      setMovimenti(response.data.movimenti);
      setGroupedData(response.data.grouped_by_partita || {});
      setTotals({
        entrate: response.data.totale_entrate,
        uscite: response.data.totale_uscite,
        scarto: response.data.totale_scarto || 0,
        grossa: response.data.totale_grossa || 0,
        media: response.data.totale_media || 0,
        bolla: response.data.totale_bolla || 0,
        count: response.data.count,
      });
    } catch (error) {
      console.error("Error loading report:", error);
      toast.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleReset = () => {
    setFilters({
      fornitore: "",
      cliente: "",
      prodotto: "",
      codice_partita: "",
      tipo: "",
      data_da: "",
      data_a: "",
    });
    setMovimenti([]);
    setGroupedData({});
    setTotals({ entrate: 0, uscite: 0, scarto: 0, grossa: 0, media: 0, bolla: 0, count: 0 });
    setGroupByPartita(false);
  };

  const handleExportPDF = () => {
    if (movimenti.length === 0) {
      toast.error("Nessun dato da esportare");
      return;
    }

    // Build filter description
    const filterDesc = [];
    if (filters.fornitore) filterDesc.push(`Fornitore: ${filters.fornitore}`);
    if (filters.cliente) filterDesc.push(`Cliente: ${filters.cliente}`);
    if (filters.prodotto) filterDesc.push(`Prodotto: ${filters.prodotto}`);
    if (filters.codice_partita) filterDesc.push(`Codice: ${filters.codice_partita}`);
    if (filters.tipo) filterDesc.push(`Tipo: ${filters.tipo}`);
    if (filters.data_da) filterDesc.push(`Da: ${formatDate(filters.data_da)}`);
    if (filters.data_a) filterDesc.push(`A: ${formatDate(filters.data_a)}`);

    // Build totals object - include Grossa and Media only if present
    const pdfTotals = {
      "Totale Entrate": totals.entrate,
      "Totale Uscite": totals.uscite,
      "Totale Scarto": totals.scarto,
    };
    
    // Add Grossa and Media only if they have values
    if (totals.grossa > 0) {
      pdfTotals["Totale Grossa"] = totals.grossa;
    }
    if (totals.media > 0) {
      pdfTotals["Totale Media"] = totals.media;
    }
    if (totals.bolla > 0) {
      pdfTotals["Totale Bolla"] = totals.bolla;
    }
    
    pdfTotals["Saldo"] = totals.entrate - totals.uscite - totals.scarto;

    // Calculate totals per product for the "Calcolo Netto" boxes
    const totaliPerProdotto = {};
    const movimentiDaAnalizzare = groupByPartita 
      ? Object.values(groupedData).flatMap(g => g.movimenti)
      : movimenti;
    
    movimentiDaAnalizzare.forEach(mov => {
      const nomeProdotto = mov.prodotto || "Sconosciuto";
      if (!totaliPerProdotto[nomeProdotto]) {
        totaliPerProdotto[nomeProdotto] = {
          grossa: 0,
          media: 0,
          bolla: 0,
          entrate: 0,
          uscite: 0,
          scarto: 0,
          percentuale: 5 // default
        };
      }
      
      if (mov.tipo === "entrata") {
        totaliPerProdotto[nomeProdotto].entrate += mov.quantita || 0;
        totaliPerProdotto[nomeProdotto].grossa += mov.quantita_grossa || 0;
        totaliPerProdotto[nomeProdotto].media += mov.quantita_media || 0;
        totaliPerProdotto[nomeProdotto].bolla += mov.quantita_bolla || 0;
      } else {
        const cliente = (mov.cliente || "").toUpperCase();
        if (cliente === "SCARTO") {
          totaliPerProdotto[nomeProdotto].scarto += mov.quantita || 0;
        } else {
          totaliPerProdotto[nomeProdotto].uscite += mov.quantita || 0;
        }
      }
    });

    // Get percentuali from prodotti array
    prodotti.forEach(p => {
      if (totaliPerProdotto[p.nome]) {
        totaliPerProdotto[p.nome].percentuale = p.percentuale_sconto ?? 5;
      }
    });

    exportReportToPDF(
      groupByPartita ? groupedData : movimenti,
      groupByPartita,
      pdfTotals,
      filterDesc.length > 0 ? filterDesc.join(" | ") : null,
      totaliPerProdotto
    );
    
    toast.success("PDF generato con successo");
  };

  const handleExportExcel = () => {
    if (movimenti.length === 0) {
      toast.error("Nessun dato da esportare");
      return;
    }

    const columns = [
      { key: "codice_partita", label: "Codice Partita", type: "text" },
      { key: "data", label: "Data", type: "date" },
      { key: "tipo", label: "Tipo", type: "text" },
      { key: "fornitore", label: "Fornitore", type: "text" },
      { key: "cliente", label: "Cliente", type: "text" },
      { key: "prodotto", label: "Prodotto", type: "text" },
      { key: "quantita_grossa", label: "Grossa (kg)", type: "number" },
      { key: "quantita_media", label: "Media (kg)", type: "number" },
      { key: "quantita_bolla", label: "Bolla (kg)", type: "number" },
      { key: "quantita", label: "Totale (kg)", type: "number" },
      { key: "note", label: "Note", type: "text" },
    ];

    // For Excel, always export flat data but sorted by partita if grouped
    const dataToExport = groupByPartita 
      ? Object.keys(groupedData).sort().flatMap(codice => groupedData[codice].movimenti)
      : movimenti;

    exportToExcel(dataToExport, columns, "Report Movimenti");
    toast.success("Excel generato con successo");
  };

  // Render grouped view
  const renderGroupedView = () => {
    const codici = Object.keys(groupedData).sort((a, b) => {
      if (sortOrder === "asc") {
        return a.localeCompare(b);
      } else {
        return b.localeCompare(a);
      }
    });
    
    return (
      <div className="space-y-6">
        {codici.map((codice) => {
          const group = groupedData[codice];
          const saldo = group.entrate - group.uscite - (group.scarto || 0);
          const hasGrossa = (group.grossa || 0) > 0;
          const hasMedia = (group.media || 0) > 0;
          const hasBolla = (group.bolla || 0) > 0;
          const hasGrossaMedia = hasGrossa || hasMedia || hasBolla;
          
          return (
            <Card key={codice} className="card-industrial">
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                      <Layers className="w-5 h-5 text-orange-500" />
                      Partita: {codice}
                    </CardTitle>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm">
                    {hasGrossa && (
                      <span className="text-emerald-600 font-mono font-bold bg-emerald-50 px-2 py-1 rounded-sm">
                        Grossa: {formatNumber(group.grossa || 0)} kg
                      </span>
                    )}
                    {hasMedia && (
                      <span className="text-teal-600 font-mono font-bold bg-teal-50 px-2 py-1 rounded-sm">
                        Media: {formatNumber(group.media || 0)} kg
                      </span>
                    )}
                    {hasBolla && (
                      <span className="text-purple-600 font-mono font-bold bg-purple-50 px-2 py-1 rounded-sm">
                        Bolla: {formatNumber(group.bolla || 0)} kg
                      </span>
                    )}
                    <span className="text-emerald-700 font-mono font-bold bg-emerald-100 px-2 py-1 rounded-sm">
                      Tot. Entrate: +{formatNumber(group.entrate)} kg
                    </span>
                    <span className="text-blue-600 font-mono font-bold bg-blue-50 px-2 py-1 rounded-sm">
                      Uscite: -{formatNumber(group.uscite)} kg
                    </span>
                    <span className="text-amber-600 font-mono font-bold bg-amber-50 px-2 py-1 rounded-sm">
                      Scarto: -{formatNumber(group.scarto || 0)} kg
                    </span>
                    <span className="text-slate-900 font-mono font-bold bg-slate-200 px-2 py-1 rounded-sm">
                      Saldo: {formatNumber(saldo)} kg
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="table-scroll-wrapper">
                  <div className="rounded-sm border border-slate-200 overflow-hidden">
                    <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Data
                        </TableHead>
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Tipo
                        </TableHead>
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Fornitore
                        </TableHead>
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Cliente
                        </TableHead>
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                          Prodotto
                        </TableHead>
                        {hasGrossa && (
                          <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                            Grossa
                          </TableHead>
                        )}
                        {hasMedia && (
                          <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                            Media
                          </TableHead>
                        )}
                        {hasBolla && (
                          <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                            Bolla
                          </TableHead>
                        )}
                        <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                          Totale
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.movimenti.map((mov, idx) => {
                        const isScarto = mov.tipo === "uscita" && mov.cliente?.toUpperCase() === "SCARTO";
                        return (
                        <TableRow 
                          key={mov.id || idx} 
                          className={isScarto 
                            ? "bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-500" 
                            : "hover:bg-slate-50/50"
                          }
                        >
                          <TableCell className="font-mono text-sm text-slate-600">
                            {formatDate(mov.data)}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              className={mov.tipo === "entrata" 
                                ? "bg-emerald-100 text-emerald-700 border-emerald-200" 
                                : mov.cliente?.toUpperCase() === "SCARTO"
                                  ? "bg-amber-100 text-amber-700 border-amber-200"
                                  : "bg-blue-100 text-blue-700 border-blue-200"
                              }
                            >
                              <span className="flex items-center gap-1">
                                {mov.tipo === "entrata" ? (
                                  <ArrowDownToLine className="w-3 h-3" />
                                ) : (
                                  <ArrowUpFromLine className="w-3 h-3" />
                                )}
                                {mov.tipo === "entrata" ? "Entrata" : mov.cliente?.toUpperCase() === "SCARTO" ? "Scarto" : "Uscita"}
                              </span>
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-700">
                            {mov.fornitore || mov.fornitore_partita || "-"}
                          </TableCell>
                          <TableCell className="text-sm text-slate-700">
                            {mov.cliente || "-"}
                          </TableCell>
                          <TableCell className="text-sm text-slate-700">
                            {mov.prodotto || "-"}
                          </TableCell>
                          {hasGrossa && (
                            <TableCell className="font-mono text-sm text-slate-600 text-right">
                              {mov.tipo === "entrata" && (mov.quantita_grossa || 0) > 0 ? formatNumber(mov.quantita_grossa) : "-"}
                            </TableCell>
                          )}
                          {hasMedia && (
                            <TableCell className="font-mono text-sm text-slate-600 text-right">
                              {mov.tipo === "entrata" && (mov.quantita_media || 0) > 0 ? formatNumber(mov.quantita_media) : "-"}
                            </TableCell>
                          )}
                          {hasBolla && (
                            <TableCell className="font-mono text-sm text-slate-600 text-right">
                              {mov.tipo === "entrata" && (mov.quantita_bolla || 0) > 0 ? formatNumber(mov.quantita_bolla) : "-"}
                            </TableCell>
                          )}
                          <TableCell className={`font-mono text-sm font-bold text-right ${
                            mov.tipo === "entrata" 
                              ? "text-emerald-600" 
                              : mov.cliente?.toUpperCase() === "SCARTO" 
                                ? "text-amber-600" 
                                : "text-blue-600"
                          }`}>
                            {mov.tipo === "entrata" ? "+" : "-"}{formatNumber(mov.quantita)} kg
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  // Render flat view
  // Get sorted movimenti for flat view
  const sortedMovimenti = [...movimenti].sort((a, b) => {
    if (sortOrder === "asc") {
      return (a.codice_partita || "").localeCompare(b.codice_partita || "");
    } else {
      return (b.codice_partita || "").localeCompare(a.codice_partita || "");
    }
  });

  const renderFlatView = () => (
    <Card className="card-industrial">
      <CardHeader>
        <CardTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-orange-500" />
          Risultati ({totals.count})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="table-scroll-wrapper">
          <div className="rounded-sm border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                    Codice
                  </TableHead>
                  <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                    Data
                  </TableHead>
                  <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                    Tipo
                  </TableHead>
                  <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                    Fornitore
                  </TableHead>
                  <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                    Cliente
                  </TableHead>
                  <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                    Prodotto
                  </TableHead>
                  <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                    Quantità
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMovimenti.map((mov, index) => {
                  const isScarto = mov.tipo === "uscita" && mov.cliente?.toUpperCase() === "SCARTO";
                  return (
                  <TableRow 
                    key={mov.id || index}
                    data-testid={`report-row-${index}`}
                    className={isScarto 
                      ? "bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-500" 
                      : "hover:bg-slate-50/50"
                    }
                  >
                    <TableCell className="font-mono text-sm font-bold text-slate-900">
                      {mov.codice_partita}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">
                      {formatDate(mov.data)}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={mov.tipo === "entrata" 
                          ? "bg-emerald-100 text-emerald-700 border-emerald-200" 
                          : isScarto
                            ? "bg-amber-100 text-amber-700 border-amber-200"
                            : "bg-red-100 text-red-700 border-red-200"
                        }
                      >
                        <span className="flex items-center gap-1">
                          {mov.tipo === "entrata" ? (
                            <ArrowDownToLine className="w-3 h-3" />
                          ) : (
                            <ArrowUpFromLine className="w-3 h-3" />
                          )}
                          {mov.tipo === "entrata" ? "Entrata" : isScarto ? "Scarto" : "Uscita"}
                        </span>
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">
                      {mov.fornitore || mov.fornitore_partita || "-"}
                    </TableCell>
                    <TableCell className={`text-sm ${isScarto ? "font-bold text-amber-700" : "text-slate-700"}`}>
                      {mov.cliente || "-"}
                      {isScarto && <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">SCARTO</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">
                      {mov.prodotto || "-"}
                    </TableCell>
                    <TableCell className={`font-mono text-sm font-bold text-right ${
                      mov.tipo === "entrata" ? "text-emerald-600" : isScarto ? "text-amber-600" : "text-red-600"
                    }`}>
                      {mov.tipo === "entrata" ? "+" : "-"}{formatNumber(mov.quantita)} kg
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="report-page" className="w-full space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
          Report
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Genera report personalizzati con filtri avanzati
        </p>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="card-industrial">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base lg:text-lg font-bold text-slate-900 flex items-center gap-2">
              <Filter className="w-5 h-5 text-orange-500" />
              Filtri di Ricerca
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="font-heading font-bold text-slate-700 text-xs">
                  Codice Partita
                </Label>
                <Input
                  data-testid="filter-codice"
                  placeholder="Es: 2025-01-0001"
                  value={filters.codice_partita}
                  onChange={(e) => setFilters({ ...filters, codice_partita: e.target.value })}
                  className="rounded-lg font-mono h-9"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="font-heading font-bold text-slate-700 text-xs">
                  Fornitore
                </Label>
                <Select
                  value={filters.fornitore}
                  onValueChange={(value) => setFilters({ ...filters, fornitore: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-fornitore" className="rounded-lg h-9">
                    <SelectValue placeholder="Tutti i fornitori" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i fornitori</SelectItem>
                    {fornitori.map((f) => (
                      <SelectItem key={f.id} value={f.nome}>
                        {f.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="font-heading font-bold text-slate-700 text-xs">
                  Cliente
                </Label>
                <Select
                  value={filters.cliente}
                  onValueChange={(value) => setFilters({ ...filters, cliente: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-cliente" className="rounded-sm">
                    <SelectValue placeholder="Tutti i clienti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i clienti</SelectItem>
                    {clienti.map((c) => (
                      <SelectItem key={c.id} value={c.nome}>
                        {c.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="font-heading font-bold text-slate-700 text-xs">
                  Prodotto
                </Label>
                <Select
                  value={filters.prodotto}
                  onValueChange={(value) => setFilters({ ...filters, prodotto: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-prodotto" className="rounded-sm">
                    <SelectValue placeholder="Tutti i prodotti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i prodotti</SelectItem>
                    {prodotti.map((p) => (
                      <SelectItem key={p.id} value={p.nome}>
                        {p.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700 text-sm">
                  Tipo Movimento
                </Label>
                <Select
                  value={filters.tipo}
                  onValueChange={(value) => setFilters({ ...filters, tipo: value === "all" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-tipo" className="rounded-sm">
                    <SelectValue placeholder="Tutti i tipi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tipi</SelectItem>
                    <SelectItem value="entrata">Entrata</SelectItem>
                    <SelectItem value="uscita">Uscita</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700 text-sm">
                  Data Da
                </Label>
                <Input
                  data-testid="filter-data-da"
                  type="date"
                  value={filters.data_da}
                  onChange={(e) => setFilters({ ...filters, data_da: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700 text-sm">
                  Data A
                </Label>
                <Input
                  data-testid="filter-data-a"
                  type="date"
                  value={filters.data_a}
                  onChange={(e) => setFilters({ ...filters, data_a: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              <Button
                data-testid="search-btn"
                onClick={handleSearch}
                disabled={loading}
                className="btn-primary rounded-sm"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? "Ricerca..." : "Cerca"}
              </Button>
              <Button
                data-testid="reset-btn"
                variant="outline"
                onClick={handleReset}
                className="rounded-sm"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Results */}
      {movimenti.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Movimenti</p>
                <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{totals.count}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Entrate</p>
                <p className="font-heading text-2xl font-bold text-emerald-700 mt-1">+{formatNumber(totals.entrate)} kg</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Uscite</p>
                <p className="font-heading text-2xl font-bold text-blue-700 mt-1">-{formatNumber(totals.uscite)} kg</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Scarto</p>
                <p className="font-heading text-2xl font-bold text-amber-700 mt-1">-{formatNumber(totals.scarto)} kg</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">Saldo</p>
                <p className="font-heading text-2xl font-bold text-orange-700 mt-1">{formatNumber(totals.entrate - totals.uscite - totals.scarto)} kg</p>
              </CardContent>
            </Card>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center space-x-2">
                <Switch
                  id="group-mode"
                  data-testid="group-by-partita-switch"
                  checked={groupByPartita}
                  onCheckedChange={setGroupByPartita}
                />
                <Label htmlFor="group-mode" className="font-medium text-sm cursor-pointer flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Raggruppa per Partita
                </Label>
              </div>
              {/* Sort Order */}
              <Button
                variant="outline"
                onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
                className="h-9 px-3 rounded-lg whitespace-nowrap"
                data-testid="sort-order-btn"
              >
                {sortOrder === "desc" ? (
                  <>
                    <ArrowDown className="w-4 h-4 mr-2" />
                    Più recenti
                  </>
                ) : (
                  <>
                    <ArrowUp className="w-4 h-4 mr-2" />
                    Più vecchie
                  </>
                )}
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="export-pdf-btn"
                onClick={handleExportPDF}
                className="btn-primary rounded-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta PDF
              </Button>
              <Button
                data-testid="export-excel-btn"
                onClick={handleExportExcel}
                variant="outline"
                className="rounded-sm"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Esporta Excel
              </Button>
            </div>
          </div>

          {/* Results View */}
          {groupByPartita ? renderGroupedView() : renderFlatView()}
        </motion.div>
      )}

      {/* Empty State */}
      {movimenti.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="card-industrial">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="font-heading text-lg font-bold text-slate-700">
                Nessun risultato
              </h3>
              <p className="text-slate-500 mt-2">
                Usa i filtri sopra per cercare i movimenti e generare un report
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
